import { useQuery } from "@tanstack/react-query";

import Sentry from "@similarweb/sentry-logger";

import { translate } from "app/filters/i18nFilter";
import { apiClient, getQueryKey } from "app/modules/api-client";
import { NEWS_LEVER_ERROR } from "app/pages/activation/components/Shared/Subscriptions/texts";
import { useToasts } from "app/pages/activation/hooks/helpers/useToasts";
import { RESOURCE_NOT_FOUND_TYPE, ResponseError } from "app/services/fetchService";

type UseCompetitorUpdatesSubscriptionStatusQueryParams = {
    activationSetId: string;
    isNewsAlertsAvailable: boolean;
};

export const useCompetitorUpdatesSubscriptionStatusQuery = ({
    activationSetId,
    isNewsAlertsAvailable,
}: UseCompetitorUpdatesSubscriptionStatusQueryParams) => {
    const { showErrorToast } = useToasts();

    return useQuery({
        queryKey: getQueryKey({
            tag: "Alerts",
            methodName: "activationCompetitorUpdatesSubscriptionsDetail",
            args: [activationSetId],
        }),
        queryFn: async () => {
            try {
                const result = await apiClient.Alerts.activationCompetitorUpdatesSubscriptionsDetail(activationSetId);

                return result;
            } catch (error) {
                if (
                    error instanceof ResponseError &&
                    error.status === 404 &&
                    error.problem?.type === RESOURCE_NOT_FOUND_TYPE
                ) {
                    return null;
                }

                Sentry.errorTracker({
                    e: error instanceof Error ? error : new Error(String(error)),
                    message: "Failed to load Alerts.activationCompetitorUpdatesSubscriptionsDetail data",
                    level: "error",
                    params: {
                        extra: {
                            activationSetId,
                        },
                    },
                });

                showErrorToast(translate(NEWS_LEVER_ERROR));
                throw error;
            }
        },
        enabled: !!activationSetId && isNewsAlertsAvailable,
        refetchOnWindowFocus: false,
        retry: false,
    });
};
